import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTenant } from "../contexts/TenantContext";
import apiClient, { consumeMagicLink, verifyTwoFactor } from "../api/apiClient";
import { loginStart, loginSuccess, loginFailure } from "../store/authSlice";
import SignInForm from "./auth/SignInForm";
import { isCheckinNext } from "./checkin/checkinPaths";
import AuthLayout, { AuthAlert } from "./auth/AuthLayout";

// Hard-coded credentials that match the backend UserDataInitializer seeds.
// Only used when REACT_APP_LOGIN_MODE=mock (local dev).
const TEST_ACCOUNT_GROUPS = [
  {
    key: "global",
    title: "Global",
    accounts: [
      {
        key: "GLOBAL_ADMIN",
        label: "Global Admin",
        username: "admin",
        password: "admin123",
      },
    ],
  },
  {
    key: "1",
    title: "South Branch Training Center",
    accounts: [
      {
        key: "tenantA-admin",
        label: "Admin: Casey Admin",
        username: "admin_a",
        password: "pass123",
      },
      {
        key: "tenantA-student-1",
        label: "Student: Alex Demo",
        username: "student_a",
        password: "pass123",
      },
      {
        key: "tenantA-instructor",
        label: "Instructor: Dr. Lee",
        username: "instructor_a",
        password: "pass123",
      },
      {
        key: "tenantA-coordinator",
        label: "Course Coordinator: Taylor Reyes",
        username: "coordinator_a",
        password: "pass123",
      },
      {
        key: "tenantA-agency",
        label: "Agency Admin: Chief Williams",
        username: "agency_a",
        password: "pass123",
      },
      {
        key: "tenantA-mixed",
        label: "Student + Instructor: Jamie Mixed",
        username: "mixed_a",
        password: "pass123",
      },
      {
        key: "tenantA-student-2",
        label: "Student: John Smith",
        username: "student2_a",
        password: "pass123",
      },
      {
        key: "tenantA-student-3",
        label: "Student: Mary Jones",
        username: "student3_a",
        password: "pass123",
      },
      {
        key: "tenantA-student-4",
        label: "Student: Bob Wilson",
        username: "student4_a",
        password: "pass123",
      },
    ],
  },
  {
    key: "2",
    title: "Hunterdon County Training Center",
    accounts: [
      {
        key: "tenantB-instructor-1",
        label: "Instructor: Instructor One",
        username: "instructor1_b",
        password: "pass123",
      },
      {
        key: "tenantB-instructor-2",
        label: "Instructor: Instructor Two",
        username: "instructor2_b",
        password: "pass123",
      },
      {
        key: "tenantB-instructor-3",
        label: "Instructor: Instructor Three",
        username: "instructor3_b",
        password: "pass123",
      },
      {
        key: "tenantB-coordinator",
        label: "Course Coordinator: Sam Coord",
        username: "coordinator_b",
        password: "pass123",
      },
      {
        key: "tenantB-agency",
        label: "Agency Admin: Director Park",
        username: "agency_b",
        password: "pass123",
      },
      {
        key: "tenantB-admin",
        label: "Admin: Morgan Admin",
        username: "admin_b",
        password: "pass123",
      },
      ...Array.from({ length: 30 }, (_, index) => ({
        key: `tenantB-student-${index + 1}`,
        label: `Student ${index + 1}`,
        username: `student${index + 1}_b`,
        password: "pass123",
      })),
    ],
  },
  {
    key: "3",
    title: "Life Guard Agency",
    accounts: [
      {
        key: "tenantC-agency",
        label: "Agency Admin: Tenant C Admin",
        username: "agency_c",
        password: "pass123",
      },
      {
        key: "tenantC-admin",
        label: "Admin: Riley Admin",
        username: "admin_c",
        password: "pass123",
      },
      {
        key: "tenantC-student-1",
        label: "Student: Chris Solo",
        username: "student1_c",
        password: "pass123",
      },
    ],
  },
];


// ── Shared page shell ────────────────────────────────────────────────────────
function LoginShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>{children}</CardContent>
        </Card>
      </Container>
    </Box>
  );
}

// ── Mock login form (local dev) ──────────────────────────────────────────────
function MockLoginForm({ doLogin, loading, error }) {
  const navigate = useNavigate();

  return (
    <LoginShell>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontFamily: "var(--font-display)" }}>
          Welcome Back
        </Typography>
        <Typography color="text.secondary">
          Sign in to access your training center account
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Global Admin */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        disabled={loading}
        onClick={() => doLogin(TEST_ACCOUNT_GROUPS[0].accounts[0])}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : "Login as Global Admin"}
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* Tenant Logins */}
      <Stack spacing={3}>
        {TEST_ACCOUNT_GROUPS.slice(1).map((group) => (
          <Box key={group.key}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 1.5,
                fontWeight: 600,
                letterSpacing: "0.08em",
              }}
            >
              {group.title}
            </Typography>
            <Stack spacing={1}>
              {group.accounts.map((account) => (
                <Button
                  key={account.key}
                  variant="outlined"
                  fullWidth
                  disabled={loading}
                  onClick={() => doLogin(account)}
                >
                  {account.label}
                </Button>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* Register Link */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Don&apos;t have an account?
        </Typography>
        <Button
          variant="text"
          onClick={() => navigate("/users/new")}
          sx={{ fontWeight: 500 }}
        >
          Register a new user
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", mt: 3 }}
      >
        This login page uses hardcoded credentials against the backend auth API
        for testing purposes.
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="text"
          size="small"
          onClick={() => navigate("/")}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Browse available courses without signing in
        </Button>
      </Box>
    </LoginShell>
  );
}

// ── Root export ──────────────────────────────────────────────────────────────
/**
 * @param {{ magicLink?: boolean }} props `magicLink` renders the /magic-link?token=... landing
 *   state instead of the sign-in form and redeems the token on mount.
 */
export default function LoginPage({ magicLink = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const magicToken = magicLink ? searchParams.get("token") : null;
  // The link is single-use and React StrictMode runs effects twice in development.
  const magicStarted = useRef(false);
  const [magicState, setMagicState] = useState(magicLink && !magicToken ? "failed" : "working");
  const [magicError, setMagicError] = useState(
    magicLink && !magicToken ? "This sign-in link is missing its token." : null,
  );
  const dispatch = useDispatch();
  const { login, domainTenant } = useTenant();
  const { loading, error } = useSelector((state) => state.auth);

  const [mfaOpen, setMfaOpen] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState(null);

  const completeLogin = (data) => {
    dispatch(loginSuccess(data));
    const firstTenant = Object.keys(data.tenantRoles || {})[0] || null;
    const user = {
      id: data.userId,
      username: data.username,
      email: data.email,
      roles: Array.isArray(data.roles) ? data.roles : Array.from(data.roles || []),
      tenantId: firstTenant,
      tenantRoles: data.tenantRoles || {},
    };
    login(user);
    const next = searchParams.get("next");
    navigate(location.state?.redirectTo || (magicLink && isCheckinNext(next) ? next : "/"));
  };

  const handleLoginResponse = (data) => {
    if (data.requires2FA) {
      setPreAuthToken(data.preAuthToken);
      setMfaOpen(true);
      dispatch(loginFailure(null));
    } else {
      completeLogin(data);
    }
  };

  useEffect(() => {
    if (!magicLink || !magicToken || magicStarted.current) return;
    magicStarted.current = true;
    consumeMagicLink(magicToken)
      .then((data) => {
        if (data.requires2FA) setMagicState("mfa");
        handleLoginResponse(data);
      })
      .catch((err) => {
        setMagicError(err?.response?.data?.message || "This sign-in link is invalid or has expired.");
        setMagicState("failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicLink, magicToken]);

  const doLogin = async (account) => {
    dispatch(loginStart());
    try {
      const res = await apiClient.post("/api/v1/auth/login", {
        username: account.username,
        password: account.password,
      });
      handleLoginResponse(res.data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Login failed. Is the backend running?";
      dispatch(loginFailure(msg));
    }
  };

  const handleMfaSubmit = async () => {
    if (!mfaCode.trim()) return;
    setMfaLoading(true);
    setMfaError(null);
    try {
      const data = await verifyTwoFactor(preAuthToken, mfaCode.trim());
      setMfaOpen(false);
      completeLogin(data);
    } catch (err) {
      setMfaError(
        err?.response?.data?.message || "Invalid code. Please try again."
      );
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <>
      {magicLink ? (
        <AuthLayout domainTenant={domainTenant}>
          {magicState === "failed" ? (
            <>
              <h1>Link not valid</h1>
              <p className="auth-sub">Request a new sign-in link, or use your password instead.</p>
              <AuthAlert>{magicError}</AuthAlert>
              <button
                type="button"
                className="auth-btn auth-btn--p"
                style={{ width: "100%" }}
                onClick={() => navigate("/login")}
              >
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <h1>{magicState === "mfa" ? "Verify it's you" : "Signing you in"}</h1>
              <p className="auth-sub">
                {magicState === "mfa"
                  ? "Enter your authentication code to finish signing in."
                  : "One moment…"}
              </p>
              {magicState === "working" && <CircularProgress size={24} aria-label="Signing you in" />}
            </>
          )}
        </AuthLayout>
      ) : false ? (
        <MockLoginForm doLogin={doLogin} loading={loading} error={error} />
      ) : (
        <SignInForm doLogin={doLogin} loading={loading} error={error} domainTenant={domainTenant} />
      )}

      <Dialog open={mfaOpen} maxWidth="xs" fullWidth onClose={() => {}}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, fontSize: "0.9rem" }}>
            Enter the 6-digit code from your authenticator app to continue.
          </Typography>
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mfaError}
            </Alert>
          )}
          <TextField
            label="Authentication Code"
            fullWidth
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && handleMfaSubmit()}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setMfaOpen(false); setMfaCode(""); setMfaError(null); }}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMfaSubmit}
            disabled={mfaLoading || mfaCode.length < 6}
            sx={{ textTransform: "none" }}
          >
            {mfaLoading ? <CircularProgress size={20} /> : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
